const TR_MAP = {
  'ı': 'i',
  'İ': 'i',
  'ş': 's',
  'Ş': 's',
  'ğ': 'g',
  'Ğ': 'g',
  'ü': 'u',
  'Ü': 'u',
  'ö': 'o',
  'Ö': 'o',
  'ç': 'c',
  'Ç': 'c'
};

const NAME_ALIASES = {
  'sicak su': 'su',
  'icme suyu': 'su',
  'su': 'su',
  'toz tuz': 'tuz',
  'sofra tuzu': 'tuz'
};

const DEFAULT_PANTRY_STAPLES = [
  'su',
  'tuz',
  'karabiber',
  'zeytinyagi',
  'aycicekyag',
  'seker',
  'ekmek',
  'sut',
  'yumurta'
];

function normalize(value = '') {
  const normalized = String(value)
    .split('')
    .map((ch) => TR_MAP[ch] || ch)
    .join('')
    .toLocaleLowerCase('tr-TR')
    .trim();

  return NAME_ALIASES[normalized] || normalized;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function extractMinutes(recipe) {
  const direct = toNumber(recipe?.cookTime || recipe?.cookingTime || recipe?.totalTime, NaN);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const str = String(recipe?.prepTime || '');
  const m = Number(str.replace(/[^\d]/g, ''));
  return Number.isFinite(m) && m > 0 ? m : 30;
}

function extractCost(recipe) {
  const snapshotTotal = toNumber(recipe?.priceSnapshot?.total, NaN);
  if (Number.isFinite(snapshotTotal) && snapshotTotal > 0) return snapshotTotal;

  const fallbackTotal = toNumber(recipe?.totalCost ?? recipe?.presentation?.totalCost, NaN);
  if (Number.isFinite(fallbackTotal) && fallbackTotal > 0) return fallbackTotal;

  const breakdownTotal = Array.isArray(recipe?.priceSnapshot?.breakdown)
    ? recipe.priceSnapshot.breakdown.reduce((sum, item) => sum + toNumber(item?.total, 0), 0)
    : 0;
  if (breakdownTotal > 0) return breakdownTotal;

  return 0;
}

function extractTasteScore(recipe) {
  const candidates = [
    recipe?.ratingAverage,
    recipe?.averageRating,
    recipe?.rating,
    recipe?.score
  ]
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v > 0);
  if (candidates.length > 0) return Math.min(5, candidates[0]);
  return 3.8;
}

function extractIngredients(recipe) {
  const raw = Array.isArray(recipe?.malzemeler) && recipe.malzemeler.length > 0
    ? recipe.malzemeler
    : (Array.isArray(recipe?.ingredients) ? recipe.ingredients : []);

  return raw
    .map((it) => {
      if (typeof it === 'string') return it;
      if (it?.name) return it.name;
      if (it?.ingredient_name) return it.ingredient_name;
      if (it?.ingredientName) return it.ingredientName;
      return '';
    })
    .map((name) => String(name || '').trim())
    .filter(Boolean);
}

function coverageFromPantry(recipe, pantrySet) {
  const ingredientNames = extractIngredients(recipe);
  if (ingredientNames.length === 0) return { total: 0, matched: 0, missing: [] };

  const effectivePantry = new Set(pantrySet);
  for (const staple of DEFAULT_PANTRY_STAPLES) {
    effectivePantry.add(normalize(staple));
  }

  const missing = ingredientNames.filter((name) => !effectivePantry.has(normalize(name)));
  const matched = ingredientNames.length - missing.length;
  return {
    total: ingredientNames.length,
    matched,
    missing
  };
}

function getExpirySignal(recipe) {
  const candidates = [recipe?.match?.minDaysLeft, recipe?.minDaysLeft, recipe?.rescueDaysLeft]
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v >= 0);
  if (candidates.length === 0) return null;
  return Math.min(...candidates);
}

function buildReasonList(recipeSignals) {
  const reasons = [];
  if (recipeSignals.expiryDays != null && recipeSignals.expiryDays <= 3) {
    if (recipeSignals.expiryDays <= 0) {
      reasons.push('Son kullanma tarihi (SKT) gelen ürün var, hemen tüketim tavsiye edilir');
    } else {
      reasons.push(`Son kullanma tarihi (SKT) yaklaşan ürün var, ${recipeSignals.expiryDays} gün içinde tüketim tavsiye edilir`);
    }
  }

  reasons.push(`Stok uyumu: %${recipeSignals.coverageRate}`);

  if (recipeSignals.missingCount === 0) {
    reasons.push('Eksik malzeme yok');
  } else {
    reasons.push(`Eksik malzeme: ${recipeSignals.missingCount}`);
  }

  reasons.push(`Hazırlık süresi: ${recipeSignals.minutes} dk`);

  if (recipeSignals.priceScore >= 60) {
    reasons.push('Maliyet skoru: uygun');
  } else if (recipeSignals.priceScore < 45) {
    reasons.push('Maliyet skoru: yüksek');
  }

  return reasons.slice(0, 4);
}

export function buildIngredientCatalog(fridges = []) {
  const pool = new Set();
  for (const fridge of fridges) {
    for (const item of fridge?.ingredients || []) pool.add(String(item).trim());
    for (const recipe of fridge?.recipes || []) {
      for (const ing of extractIngredients(recipe)) pool.add(ing);
    }
  }
  return Array.from(pool).filter(Boolean).sort((a, b) => a.localeCompare(b, 'tr'));
}

export function searchIngredientSuggestions(query, selectedIngredients = [], catalog = []) {
  const q = normalize(query);
  if (!q) return [];
  const selectedSet = new Set((selectedIngredients || []).map((x) => normalize(x)));
  return (catalog || [])
    .filter((item) => !selectedSet.has(normalize(item)))
    .filter((item) => normalize(item).includes(q))
    .slice(0, 8);
}

export function rankLandingRecipes(recipes = [], selectedIngredients = []) {
  const pantrySet = new Set((selectedIngredients || []).map((x) => normalize(x)));
  const candidateList = Array.isArray(recipes) ? recipes : [];

  const raw = candidateList.map((recipe) => {
    const coverage = coverageFromPantry(recipe, pantrySet);
    const coverageRate = coverage.total > 0 ? Math.round((coverage.matched / coverage.total) * 100) : 0;
    const minutes = extractMinutes(recipe);
    const cost = extractCost(recipe);
    const taste = extractTasteScore(recipe);
    const expiryDays = getExpirySignal(recipe);
    return {
      recipe,
      coverageRate,
      missingCount: coverage.missing.length,
      missingItems: coverage.missing,
      minutes,
      cost,
      taste,
      expiryDays
    };
  });

  const positiveCosts = raw.map((r) => r.cost).filter((c) => c > 0);
  const medianCost = positiveCosts.length > 0
    ? positiveCosts.sort((a, b) => a - b)[Math.floor(positiveCosts.length / 2)]
    : 0;

  const scored = raw.map((r) => {
    const priceScore = medianCost > 0
      ? Math.max(0, Math.min(100, Math.round(((medianCost - r.cost) / medianCost) * 100 + 50)))
      : 50;
    const flavorNorm = Math.round((r.taste / 5) * 100);
    const flavorPriceScore = Math.round((flavorNorm * 0.65) + (priceScore * 0.35));
    const expiryBoost = r.expiryDays != null && r.expiryDays <= 3 ? 12 : 0;
    const fastBoost = r.minutes <= 30 ? 8 : 0;
    const score = (r.coverageRate * 0.55) + ((100 - Math.min(100, r.missingCount * 20)) * 0.2) + (priceScore * 0.15) + fastBoost + expiryBoost;

    const signals = {
      coverageRate: r.coverageRate,
      missingCount: r.missingCount,
      minutes: r.minutes,
      priceScore,
      flavorPriceScore,
      expiryDays: r.expiryDays
    };

    return {
      ...r,
      priceScore,
      flavorPriceScore,
      score,
      facts: {
        coverageRate: r.coverageRate,
        missingCount: r.missingCount,
        missingItems: r.missingItems,
        minutes: r.minutes,
        cost: r.cost,
        expiryDays: r.expiryDays
      },
      reasons: buildReasonList(signals)
    };
  });

  return scored.sort((a, b) => b.score - a.score);
}
